import React, { useState, useCallback, useEffect, ChangeEvent } from 'react';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, SxProps, Theme } from '@mui/material';
import lodashDebounce from 'lodash/debounce';

type QuickSearchProps = {
  id: string;
  initialValue?: string;
  minWidth?: number;
  onChange: (value: string) => void;
  onChangeDelay?: number;
  placeholder: string | undefined;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

const QuickSearch: React.FC<QuickSearchProps> = ({
  id,
  initialValue = '',
  minWidth = 300,
  onChange,
  onChangeDelay = 500,
  placeholder,
  fullWidth = false,
  sx,
}) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [showClear, setShowClear] = useState(initialValue.length > 0 ? 'flex' : 'none');

  const debouncedOnChange = useCallback(
    lodashDebounce((val) => {
      onChange(val);
    }, onChangeDelay),
    []
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowClear(event.target.value === '' ? 'none' : 'flex');
    setSearchQuery(event.target.value);
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowClear('none');
  };

  useEffect(() => {
    debouncedOnChange(searchQuery);
  }, [debouncedOnChange, searchQuery]);

  useEffect(() => {
    setSearchQuery(initialValue);
    setShowClear(initialValue.length > 0 ? 'flex' : 'none');
  }, [initialValue]);

  return (
    <TextField
      id={id}
      data-testid='test-quick-search'
      size='small'
      variant='outlined'
      value={searchQuery}
      onChange={handleChange}
      placeholder={placeholder}
      fullWidth={fullWidth}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position='end'>
            <IconButton
              style={{ display: showClear }}
              onClick={handleClear}
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ p: 0, minWidth: minWidth, ...sx }}
    />
  );
};

export default QuickSearch;
